/**
 * Defaults canónicos del Catálogo de Riesgos.
 *
 * Vienen del PEP FAC Capítulo Riesgos v1.0 (Mayo 2026). Estos datos
 * se siembran la primera vez que el tenant abre el módulo de riesgos.
 * El ADMIN_PMIS puede modificarlos después desde
 * `/configuracion/riesgos`.
 */

import type {
  CategoriaRbs,
  EscalaProbabilidadItem,
  EscalaImpactoItem,
  UmbralNivel,
} from "@app-types/entities";

// ── RBS — Risk Breakdown Structure (PEP §7) ────────────────────────

export const RBS_DEFAULT: readonly CategoriaRbs[] = [
  // 1. Technical
  {
    codigo: "1.1",
    nivel1: "Technical",
    nivel2: "Ingeniería y Diseño",
    ejemplos: ["Errores de diseño", "Especificaciones incompletas"],
  },
  {
    codigo: "1.2",
    nivel1: "Technical",
    nivel2: "Construcción y Montaje",
    ejemplos: ["Defectos de soldadura", "Materiales fuera de norma"],
  },
  {
    codigo: "1.3",
    nivel1: "Technical",
    nivel2: "Comisionamiento / PEM",
    ejemplos: ["Fallas en PH", "Cortocircuitos", "Problemas de calibración"],
  },
  {
    codigo: "1.4",
    nivel1: "Technical",
    nivel2: "HSE / Seguridad de Procesos",
    ejemplos: ["Incendio", "Fallas en protección", "Incidentes de bloqueo"],
  },
  // 2. External
  {
    codigo: "2.1",
    nivel1: "External",
    nivel2: "Proveedores / Supply Chain",
    ejemplos: ["Retrasos en entregas", "Equipos en deterioro"],
  },
  {
    codigo: "2.2",
    nivel1: "External",
    nivel2: "Regulatorio / Ambiental",
    ejemplos: ["Cambios normativa ANLA", "Demoras en licencias"],
  },
  {
    codigo: "2.3",
    nivel1: "External",
    nivel2: "Social / Comunidades",
    ejemplos: ["Bloqueos de vías", "Conflictos por servidumbres"],
  },
  {
    codigo: "2.4",
    nivel1: "External",
    nivel2: "Security",
    ejemplos: ["Orden público", "Restricciones de acceso"],
  },
  // 3. Organizational
  {
    codigo: "3.1",
    nivel1: "Organizational",
    nivel2: "Governance / Gestión",
    ejemplos: ["PEP incompleto", "Brechas de madurez"],
  },
  {
    codigo: "3.2",
    nivel1: "Organizational",
    nivel2: "Recursos Humanos",
    ejemplos: ["Rotación de personal", "Falta de competencias"],
  },
  {
    codigo: "3.3",
    nivel1: "Organizational",
    nivel2: "Financiero",
    ejemplos: ["Sobrecostos", "Cambios de alcance sin control"],
  },
  // 4. Project Management
  {
    codigo: "4.1",
    nivel1: "Project Management",
    nivel2: "Adquisiciones",
    ejemplos: ["PRs incompletas", "Demoras proceso ABAS"],
  },
  {
    codigo: "4.2",
    nivel1: "Project Management",
    nivel2: "Cronograma",
    ejemplos: ["Retrasos por dependencias", "Tiempos ABAS no integrados"],
  },
  {
    codigo: "4.3",
    nivel1: "Project Management",
    nivel2: "Comunicaciones",
    ejemplos: ["Incongruencia entre turnos", "Falta de trazabilidad"],
  },
] as const;

// ── Escala de Probabilidad (PEP §8.1) ──────────────────────────────

export const ESCALA_PROBABILIDAD_DEFAULT: readonly EscalaProbabilidadItem[] = [
  { valor: 1, nivel: "Raro", descripcion: "Circunstancias excepcionales", rangoPct: "<5%" },
  { valor: 2, nivel: "Improbable", descripcion: "Podría ocurrir", rangoPct: "5-20%" },
  { valor: 3, nivel: "Posible", descripcion: "Ha ocurrido antes", rangoPct: "20-50%" },
  { valor: 4, nivel: "Probable", descripcion: "Probablemente ocurrirá", rangoPct: "50-80%" },
  { valor: 5, nivel: "Casi Seguro", descripcion: "Se espera que ocurra", rangoPct: ">80%" },
] as const;

// ── Escala de Impacto por Objetivo (PEP §8.2) ──────────────────────

export const ESCALA_IMPACTO_DEFAULT = {
  costo: [
    { valor: 1, nivel: "Muy Bajo", criterio: "<5%" },
    { valor: 2, nivel: "Bajo", criterio: "5-10%" },
    { valor: 3, nivel: "Moderado", criterio: "10-20%" },
    { valor: 4, nivel: "Alto", criterio: "20-40%" },
    { valor: 5, nivel: "Muy Alto", criterio: ">40%" },
  ] as EscalaImpactoItem[],
  cronograma: [
    { valor: 1, nivel: "Muy Bajo", criterio: "<1 semana" },
    { valor: 2, nivel: "Bajo", criterio: "1-4 semanas" },
    { valor: 3, nivel: "Moderado", criterio: "1-3 meses" },
    { valor: 4, nivel: "Alto", criterio: "3-6 meses" },
    { valor: 5, nivel: "Muy Alto", criterio: ">6 meses" },
  ] as EscalaImpactoItem[],
  alcance: [
    { valor: 1, nivel: "Muy Bajo", criterio: "Apenas perceptible" },
    { valor: 2, nivel: "Bajo", criterio: "Áreas menores" },
    { valor: 3, nivel: "Moderado", criterio: "Áreas mayores" },
    { valor: 4, nivel: "Alto", criterio: "Inaceptable" },
    { valor: 5, nivel: "Muy Alto", criterio: "Inviable" },
  ] as EscalaImpactoItem[],
  calidad: [
    { valor: 1, nivel: "Muy Bajo", criterio: "Secundaria" },
    { valor: 2, nivel: "Bajo", criterio: "No críticos" },
    { valor: 3, nivel: "Moderado", criterio: "Aprob. sponsor" },
    { valor: 4, nivel: "Alto", criterio: "No utilizable" },
    { valor: 5, nivel: "Muy Alto", criterio: "Rechazado" },
  ] as EscalaImpactoItem[],
  hse: [
    { valor: 1, nivel: "Muy Bajo", criterio: "Primeros auxilios" },
    { valor: 2, nivel: "Bajo", criterio: "Lesión menor" },
    { valor: 3, nivel: "Moderado", criterio: "Lesión seria" },
    { valor: 4, nivel: "Alto", criterio: "Incapacidad" },
    { valor: 5, nivel: "Muy Alto", criterio: "Fatalidad" },
  ] as EscalaImpactoItem[],
};

// ── Umbrales de Nivel (PEP §8.4) ───────────────────────────────────

export const UMBRALES_NIVEL_DEFAULT: readonly UmbralNivel[] = [
  {
    scoreMin: 15,
    scoreMax: 25,
    nivel: "CRITICO",
    escalamiento: ["Sponsor", "DRB (GTE)"],
  },
  {
    scoreMin: 13,
    scoreMax: 14,
    nivel: "ALTO",
    escalamiento: ["Project Leader", "Sponsor"],
  },
  {
    scoreMin: 10,
    scoreMax: 12,
    nivel: "MEDIO-ALTO",
    escalamiento: ["Project Leader"],
  },
  {
    scoreMin: 5,
    scoreMax: 9,
    nivel: "MODERADO",
    escalamiento: ["Project Leader"],
  },
  {
    scoreMin: 1,
    scoreMax: 4,
    nivel: "BAJO",
    escalamiento: ["Supervisor"],
  },
] as const;

/** Computa el nivel a partir de un score, usando los umbrales. */
export function nivelDesdeScore(
  score: number,
  umbrales: readonly UmbralNivel[] = UMBRALES_NIVEL_DEFAULT,
): UmbralNivel["nivel"] {
  for (const u of umbrales) {
    if (score >= u.scoreMin && score <= u.scoreMax) return u.nivel;
  }
  return "BAJO";
}
